import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// Platform Admin > Marketing: SnowTrack's own product email to owners who
// opted in (server/services/marketingEmail.js, migration 111).
//
// The count, the subscriber CSV, compose (plain text with {{first_name}} and
// {{company_name}}), a preview sent to the super admin's own address, the send
// to every opted-in owner, and the log of sends. One send runs at a time and
// there is no scheduling. The send carries the count this screen showed, and
// the server refuses it when the audience has changed since.

const API = process.env.REACT_APP_API_URL;

const when = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const STATUS_LABELS = { sending: 'Sending', sent: 'Sent', failed: 'Failed' };

function AdminMarketing({ refreshKey }) {
  const [summary, setSummary] = useState(null);
  const [sends, setSends] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [openSend, setOpenSend] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/api/admin/marketing/summary`),
        axios.get(`${API}/api/admin/marketing/sends`),
      ]);
      setSummary(s.data);
      setSends(l.data.sends || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load marketing.');
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const exportCsv = async () => {
    setBusy('csv');
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/marketing/subscribers.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `snowtrack-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export subscribers. Please try again.');
    } finally {
      setBusy('');
    }
  };

  const sendPreview = async () => {
    setBusy('preview');
    setError('');
    setNotice('');
    try {
      const res = await axios.post(`${API}/api/admin/marketing/preview`, { subject, body });
      setPreview(res.data);
      setNotice(`Preview sent to ${res.data.to}.`);
    } catch (err) {
      setError(err.response?.data?.error || 'The preview could not be sent.');
    } finally {
      setBusy('');
    }
  };

  const send = async () => {
    setBusy('send');
    setError('');
    setNotice('');
    try {
      const res = await axios.post(`${API}/api/admin/marketing/send`, { subject, body, confirmCount: summary.optedInOwners });
      setNotice(res.data.message + (res.data.failed ? ` ${res.data.failed} failed; see the log.` : ''));
      setConfirming(false);
      setSubject('');
      setBody('');
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.error || 'The send failed.');
      setConfirming(false);
    } finally {
      setBusy('');
      load();
    }
  };

  const showSend = async (id) => {
    if (openSend && openSend.send.id === id) { setOpenSend(null); return; }
    try {
      const res = await axios.get(`${API}/api/admin/marketing/sends/${id}`);
      setOpenSend(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load the send.');
    }
  };

  if (!summary) {
    return (
      <div className="feature-requests-section">
        <h2>Marketing</h2>
        {error ? <div className="alert alert-error">{error}</div> : <p>Loading...</p>}
      </div>
    );
  }

  const missing = summary.missing || [];
  const ready = missing.length === 0 && summary.emailConfigured;
  const canCompose = subject.trim() !== '' && body.trim() !== '';
  const audience = summary.optedInOwners;

  return (
    <div className="feature-requests-section">
      <h2>Marketing</h2>
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <div className="card mb-lg">
        <p style={{ fontSize: '1.25rem', margin: 0 }}>
          <strong data-testid="marketing-count">{audience}</strong> owner{audience === 1 ? '' : 's'} opted in to product email
        </p>
        <p className="text-muted" style={{ fontSize: '0.875rem' }}>
          Active owners who ticked the signup box or turned it on in Settings, Account. The platform company and ZZ-APITEST test companies are left out.
        </p>
        <button type="button" className="btn btn-secondary" onClick={exportCsv} disabled={busy === 'csv'}>
          {busy === 'csv' ? 'Exporting...' : 'Export subscribers (CSV)'}
        </button>
        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '12px' }}>
          Postal address in every email: {summary.postalAddress || <strong>not set (PLATFORM_POSTAL_ADDRESS)</strong>}
        </p>
        {missing.length > 0 && (
          <div className="alert alert-error" data-testid="marketing-missing">
            Preview and send are off until {missing.join(' and ')} {missing.length === 1 ? 'is' : 'are'} set on the server (Railway).
          </div>
        )}
        {missing.length === 0 && !summary.emailConfigured && (
          <div className="alert alert-error">Preview and send are off: SendGrid is not configured.</div>
        )}
      </div>

      <div className="card mb-lg">
        <h3>Compose</h3>
        <div className="form-group">
          <label className="form-label" htmlFor="marketingSubject">Subject</label>
          <input id="marketingSubject" className="form-input" maxLength={200} value={subject}
            onChange={(e) => { setSubject(e.target.value); setPreview(null); setConfirming(false); }} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="marketingBody">Message (plain text)</label>
          <textarea id="marketingBody" className="form-input" rows={12} maxLength={20000} value={body}
            onChange={(e) => { setBody(e.target.value); setPreview(null); setConfirming(false); }} />
          <p className="text-muted" style={{ fontSize: '0.875rem' }}>
            Merge fields: {'{{first_name}}'} (or "there" when blank) and {'{{company_name}}'}. Links starting https:// become clickable.
            The unsubscribe link, the reason the owner gets it and the postal address are added to every email.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary" onClick={sendPreview} disabled={!ready || !canCompose || busy !== ''}>
            {busy === 'preview' ? 'Sending preview...' : 'Send preview to me'}
          </button>
          {!confirming ? (
            <button type="button" className="btn btn-primary" onClick={() => setConfirming(true)}
              disabled={!ready || !canCompose || audience === 0 || summary.sending || busy !== ''}>
              Send...
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-danger" onClick={send} disabled={busy !== ''}>
                {busy === 'send' ? 'Sending...' : `Send to ${audience} owner${audience === 1 ? '' : 's'}`}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirming(false)} disabled={busy !== ''}>Cancel</button>
            </>
          )}
        </div>
        {summary.sending && <p className="text-muted">A send is in progress. Another can start when it finishes.</p>}
        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '8px' }}>
          The preview goes to your own address with a real unsubscribe link: clicking it unsubscribes you.
        </p>
        {preview && (
          <div className="card" style={{ background: '#f8fafc', marginTop: '12px' }} data-testid="marketing-preview">
            <p><strong>{preview.subject}</strong></p>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{preview.text}</pre>
          </div>
        )}
      </div>

      <div className="card mb-lg">
        <h3>Sends</h3>
        {sends.length === 0 ? <p className="text-muted">No sends yet.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr><th>Started</th><th>Subject</th><th>By</th><th>Sent</th><th>Failed</th><th>Status</th></tr>
              </thead>
              <tbody>
                {sends.map((s) => (
                  <tr key={s.id} onClick={() => showSend(s.id)} style={{ cursor: 'pointer' }}>
                    <td>{when(s.started_at)}</td>
                    <td>{s.subject}</td>
                    <td>{s.sent_by_name || ''}</td>
                    <td>{s.sent_count} of {s.recipient_count}</td>
                    <td>{s.failed_count}</td>
                    <td>{STATUS_LABELS[s.status] || s.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {openSend && (
          <div style={{ marginTop: '12px' }} data-testid="marketing-send-detail">
            <h4>{openSend.send.subject}</h4>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{openSend.send.body}</pre>
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead><tr><th>Name</th><th>Company</th><th>Email</th><th>Status</th><th>Error</th></tr></thead>
                <tbody>
                  {openSend.recipients.map((r) => (
                    <tr key={r.id}>
                      <td>{r.name || ''}</td>
                      <td>{r.company_name || ''}</td>
                      <td>{r.email || '(deleted)'}</td>
                      <td>{STATUS_LABELS[r.status] || r.status}</td>
                      <td>{r.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminMarketing;
